import type { ViewerPosition } from '../contracts/models';

export interface ContentCoordinateOptions {
  isPdfLayout?: boolean;
  position: ViewerPosition;
  scale: number;
}

/**
 * Convert screen coordinates to SVG viewBox user space, accounting for
 * preserveAspectRatio="xMidYMid meet" letterboxing.
 */
const clientToSvgUserPointExplicit = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): ViewerPosition | null => {
  const rect = svg.getBoundingClientRect();
  const viewBox = svg.viewBox.baseVal;

  if (rect.width <= 0 || rect.height <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
    return null;
  }

  const meetScale = Math.min(rect.width / viewBox.width, rect.height / viewBox.height);
  const renderedWidth = viewBox.width * meetScale;
  const renderedHeight = viewBox.height * meetScale;
  const offsetX = rect.left + (rect.width - renderedWidth) / 2;
  const offsetY = rect.top + (rect.height - renderedHeight) / 2;

  return {
    x: (clientX - offsetX) / meetScale + viewBox.x,
    y: (clientY - offsetY) / meetScale + viewBox.y,
  };
};

/**
 * Map screen coordinates to the SVG viewBox / user coordinate space before
 * the layout group's pan and zoom transform.
 */
export const clientToSvgUserPoint = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): ViewerPosition | null => {
  if (svg.viewBox.baseVal.width > 0 && svg.viewBox.baseVal.height > 0) {
    return clientToSvgUserPointExplicit(svg, clientX, clientY);
  }

  const screenCtm = svg.getScreenCTM();
  if (!screenCtm) return null;

  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const userPoint = point.matrixTransform(screenCtm.inverse());
  return { x: userPoint.x, y: userPoint.y };
};

/**
 * Map a screen-space pointer position to content coordinates inside the
 * panned and zoomed layout group.
 *
 * Image layouts use SVG viewBox coordinates, including letterboxing. PDF
 * overlays use container pixel coordinates. Both paths then undo the same
 * translate(position) and scale(scale) transform.
 */
export const clientToContentPoint = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  options: ContentCoordinateOptions,
): ViewerPosition | null => {
  const { isPdfLayout = false, position, scale } = options;
  if (!Number.isFinite(scale) || scale === 0) return null;

  let parentX: number;
  let parentY: number;

  if (isPdfLayout) {
    const rect = svg.getBoundingClientRect();
    parentX = clientX - rect.left;
    parentY = clientY - rect.top;
  } else {
    const userPoint = clientToSvgUserPoint(svg, clientX, clientY);
    if (!userPoint) return null;
    parentX = userPoint.x;
    parentY = userPoint.y;
  }

  return {
    x: Math.round((parentX - position.x) / scale),
    y: Math.round((parentY - position.y) / scale),
  };
};
